import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { EmptyState, KeyValue, Section, Spinner } from '@zt/ui';
import type { ai } from '@zt/core';
import { api, type Jsonify } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { useMoney } from '../../lib/money';

/**
 * Tahmine dayalı müşteri değeri (F9-09): RFM sipariş özetinden önümüzdeki 12 ayın beklenen harcaması. Yapay zekâ
 * kullanmaz; model ve varsayımlar ekranda açıklanır.
 */

type R<F extends (...a: never[]) => unknown> = Jsonify<Awaited<ReturnType<F>>>;
type Overview = R<typeof ai.ltvOverview>;

export function LtvPage() {
  const t = useT();
  const { formatNumber, formatDate } = useI18n();
  const money = useMoney();
  const q = useQuery({ queryKey: ['ltv'], queryFn: () => api.get<Overview>('/v1/admin/ai/ltv') });
  const d = q.data;
  return (
    <PageFrame helpKey="page.ai_ltv">
      <p className="max-w-[70ch] text-sm text-ink-2">{t('ai.ltv.lead')}</p>
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : !d.computedAt || !d.customers ? (
        <EmptyState title={t('ai.ltv.empty')} description={t('ai.ltv.emptyLead')} />
      ) : (
        <>
          <Section title={t('ai.ltv.summaryTitle')} action={<HelpTip k="ltv.model" />}>
            <KeyValue
              items={[
                { label: t('ai.ltv.computedAt'), value: formatDate(d.computedAt, true) },
                {
                  label: t('ai.ltv.customers'),
                  value: t('ai.ltv.modeledOf', { modeled: d.modeled, total: d.customers }),
                },
                { label: t('ai.ltv.total'), value: money(d.totalPredictedMinor, d.currency) },
                {
                  label: t('ai.ltv.repeatRate'),
                  value:
                    d.base.repeatRateBps === null
                      ? t('ai.ltv.repeatUnknown', { min: 20, count: d.base.baseCustomers })
                      : t('ai.ltv.repeatValue', {
                          rate: formatNumber(d.base.repeatRateBps / 100, 1),
                          days: d.base.medianIntervalDays ?? '—',
                        }),
                },
                {
                  label: t('ai.ltv.top10'),
                  value: d.top10ShareBps === null ? '—' : `%${formatNumber(d.top10ShareBps / 100, 1)}`,
                },
                ...(d.cac
                  ? [
                      {
                        label: t('ai.ltv.cac'),
                        value: t('ai.ltv.cacValue', {
                          cac: money(d.cac.cacMinor, d.cac.currency),
                          ltv: money(d.cac.newCustomerLtvMinor, d.cac.currency),
                        }),
                      },
                    ]
                  : []),
              ]}
            />
          </Section>
          <Section title={t('ai.ltv.segmentsTitle')} action={<HelpTip k="ltv.segments" />}>
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full min-w-[32rem] text-sm">
                <caption className="sr-only">{t('ai.ltv.segmentsTitle')}</caption>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('ai.ltv.segment')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('ai.ltv.customers')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('ai.ltv.avgHistoric')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('ai.ltv.avgPredicted')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {d.segments.map((s) => (
                    <tr key={s.segment}>
                      <th scope="row" className="px-3 py-2 text-start font-normal">
                        {t(`admin.segments.groups.${s.segment}`)}
                      </th>
                      <td className="px-3 py-2 text-end tabular-nums">{formatNumber(s.customers)}</td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {money(s.avgHistoricMinor, d.currency)}
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {money(s.avgPredictedMinor, d.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          <Section title={t('ai.ltv.topTitle')}>
            <ol className="divide-y divide-line rounded-md border border-line text-sm">
              {d.top.map((c) => (
                <li key={c.customerId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2">
                  <span className="min-w-0 flex-1">
                    {c.name ? (
                      <Link
                        to="/musteriler/$id"
                        params={{ id: c.customerId }}
                        className="text-pine underline underline-offset-2"
                      >
                        {c.name}
                      </Link>
                    ) : (
                      <span className="text-ink-3">{t('ai.ltv.hiddenName')}</span>
                    )}
                    <span className="text-ink-3">
                      {' '}
                      · {t(`admin.segments.groups.${c.segment}`)} · {t('ai.ltv.orders', { count: c.orders })}
                    </span>
                  </span>
                  <span className="tabular-nums">
                    {t('ai.ltv.predictedLine', {
                      value: money(c.predictedMinor, d.currency),
                      orders: formatNumber(c.expectedOrdersPermille / 1000, 1),
                    })}
                  </span>
                </li>
              ))}
            </ol>
          </Section>
        </>
      )}
    </PageFrame>
  );
}
